import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, filter, withLatestFrom, tap, throttleTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ReviewTargetsActions, RouterActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { HttpApiService } from '@services/http-api.service';
import { ReviewTargetsService } from '@services/review-targets/review-targets.service';
import { ReviewTargetModel, RequestModel, LocationModel } from '@models/index';
import { RouterFacadeService } from '@services/router-facade.service';
import { MessageService } from 'primeng/api';
import { ReviewTargetsFacadeService } from '@services/review-targets/review-targets-facade.service';
import { GMBFailures } from '@enums/GMBFailures.enum';
import { GMBFacadeService } from '@services/gmb/gmb-facade.service';
@Injectable()
export class ReviewTargetsEffects {

  searchAllReviewTargets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.searchAllReviewTargets),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as { data: ReviewTargetModel[], total: number }),
            map(data => ReviewTargetsActions.searchReviewTargetsSuccess({ ...data })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  searchReviewTargetsByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.searchReviewTargetsByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          params: request.params + `&id=${location.id}`
        })
      ),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as { data: ReviewTargetModel[], total: number }),
            map(data => ReviewTargetsActions.searchReviewTargetsSuccess({ ...data })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  searchAllReviewTargetsByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.searchAllReviewTargetsByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          params: request.params + `/${location.id}`
        })
      ),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as ReviewTargetModel[]),
            map(reviewTargets => ReviewTargetsActions.searchAllReviewTargetsByLocationSuccess({ reviewTargets })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  createReviewTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.saveReviewTarget),
      map(action => action.request),
      throttleTime(4000),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data) => data as ReviewTargetModel),
            tap(data => this.launchToastMssg('success', 'Create Review Target', `The review target ${data.name} has been created`)),
            map(reviewTarget => ReviewTargetsActions.saveReviewTargetSuccess({ reviewTarget })),
            tap(() => this.routerFacadeService.navigateTo$('admin/review-target')),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  editReviewTargetByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.saveReviewTargetByLocation),
      map(action => {return {...{}, request: action.request, showToastMessage: action.showToastMessage}}),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (data: {request: RequestModel, showToastMessage: boolean}, location: LocationModel) => ({
            ... data,
            request: {
              ...data.request,
              payload: { ...data.request.payload, locationId: location.id}
            },
            showToastMessage: data.showToastMessage
          })
      ),
      switchMap(data =>  {
        const showToastMessage = data.showToastMessage
        return  this.httpApiService
          .postRequest(data.request)
          .pipe(
            map((data) => data as ReviewTargetModel),
            tap(data => showToastMessage ? this.launchToastMssg('success', 'Edit Review Target', `The review target ${data.name} has been edited`) : ''),
            map(reviewTarget => ReviewTargetsActions.saveReviewTargetSuccess({ reviewTarget })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      }
      )
    )
  );

  deleteReviewTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.deleteReviewTarget),
      map(action => action.request),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data: { data: string }) => data.data as string),
            tap(data => this.launchToastMssg('success', 'Delete Review Target', 'The review target has been deleted')),
            map(reviewTargetId => ReviewTargetsActions.deleteReviewTargetSuccess({ reviewTargetId })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  searchAllReviewTargetsByQueryByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.searchAllReviewTargetsByQueryByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel)=>({
          ...request,
          params: request.params + `&locationId=${location.id}`
        })
      ),
      switchMap(request =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map((data) => data as { data: ReviewTargetModel[], total: number }),
            map(data => ReviewTargetsActions.searchReviewSourcesQuerySuccess({ ...data })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  switchAllReviewTargets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.switchAllReviewTargets),
      map(action => action.request),
      switchMap(request =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map((data) => data as { data: ReviewTargetModel[], total: number }),
            map(data => ReviewTargetsActions.searchReviewTargetsSuccess({ ...data })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  switchAllReviewTargetsByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.switchAllReviewTargetsByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          params: request.params + `&id=${location.id}`
        })
      ),
      switchMap(request =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map((data) => data as { data: ReviewTargetModel[], total: number }),
            map(data => ReviewTargetsActions.searchReviewTargetsSuccess({ ...data })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  addReviewSourceToLocation = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.addReviewSourceToLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          payload: {
            reviewTarget: request.payload,
            locationId: location.id
          }
        })
      ),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data) => data as ReviewTargetModel),
            tap(data => this.launchToastMssg('success', 'Add Review Source to Location', 'The review target has been added to location')),
            tap(()=>this.reviewTargetsFacadeService.searchReviewTargetsByLocation({page: 0, size: 100})),
            map(reviewSource => ReviewTargetsActions.addReviewSourceToLocationSuccess({ reviewSource })),
            catchError(error => {
              if (error.error.message == GMBFailures.AuthFailure){
                this.GMBFacadeService.authenticateWithGoogle(null)
                return of(ReviewTargetsActions.reviewTargetError({ error: error.error.message }))
              }
              return of(ReviewTargetsActions.reviewTargetError({ error: error.message }))
              }
            )
          )
      )
    )
  );

  deleteReviewTargetByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.deleteReviewTargetByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          payload: {
            reviewTargetId: request.payload,
            locationId: location.id
          }
        })
      ),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data: { data: string }) => data.data as string),
            tap(data => this.launchToastMssg('success', 'Remove Review Source from Location', 'The review target has been removed from location')),
            tap(()=>this.reviewTargetsFacadeService.searchReviewTargetsByLocation({page: 0, size: 100})),
            map(reviewTargetId => ReviewTargetsActions.deleteReviewTargetSuccess({ reviewTargetId })),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  searchLocationReviewTargetWithErrors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.searchLocationReviewTargetWithErrors),
      map(action => action.request),
      switchMap((request) =>
        this.httpApiService.getRequest(request)
          .pipe(
            map((data) => data as boolean),
            map(data => ReviewTargetsActions.searchLocationReviewTargetWithErrorsSuccses({locationReviewTargetWithErrors:data})),
            catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
          )
      )
    )
  );

  updateFacebookReviewTargetByLocation$ = createEffect(() =>
  this.actions$.pipe(
    ofType(ReviewTargetsActions.updateFacebookReviewTargetByLocation),
    map(action => action.request),
    withLatestFrom(
      this.store.select(fromApp.getCurrentLocation),
      (request: RequestModel, location: LocationModel) => ({
        ...request,
        payload: {
          deleteReviewTarget: {
            reviewTargetId: request.payload.reviewTargetId,
            locationId: location.id
          },
          locationReviewTarget: {
            reviewTarget: request.payload.reviewTarget,
            locationId: location.id
          }
        }
      })
    ),
    switchMap(request =>
      this.httpApiService
        .postRequest(request)
        .pipe(
          map((data) => data as ReviewTargetModel),
          tap(data => this.launchToastMssg('success', 'Success!', `Your Facebook account is connected`)),
          withLatestFrom(
            this.store.select(fromApp.getUIReviewTargets),
            (data: ReviewTargetModel, uiReviewTarget: ReviewTargetModel[]) => ({
              ... data,
              priority: uiReviewTarget.find(reviewTarget => reviewTarget.id == data.id).priority,
              isReviewTarget: uiReviewTarget.find(reviewTarget => reviewTarget.id == data.id).isReviewTarget
            })
          ),
          map(data => ReviewTargetsActions.updateFacebookReviewTargetByLocationSuccess({ reviewTarget : data })),
          catchError(error => {
            return of(ReviewTargetsActions.reviewTargetError({ error: error.message }))
          }
          )
        )
    )
  )
);

updateFacebookReviewTargetByLocationSuccess$ = createEffect(() =>
this.actions$.pipe(
  ofType(ReviewTargetsActions.updateFacebookReviewTargetByLocationSuccess),
  tap(data=>{
    this.reviewTargetsFacadeService.editReviewTargetByLocation(data, false);
  })
),
{ dispatch: false }
);


  updateGMBReviewTargetByLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.updateGMBReviewTargetByLocation),
      map(action => action.request),
      withLatestFrom(
        this.store.select(fromApp.getCurrentLocation),
        (request: RequestModel, location: LocationModel) => ({
          ...request,
          payload: {
            deleteReviewTarget: {
              reviewTargetId: request.payload.reviewTargetId,
              locationId: location.id
            },
            locationReviewTarget: {
              reviewTarget: request.payload.reviewTarget,
              locationId: location.id
            }
          }
        })
      ),
      switchMap(request =>
        this.httpApiService
          .postRequest(request)
          .pipe(
            map((data) => data as ReviewTargetModel),
            tap(data => this.launchToastMssg('success', 'Success!', `Your Google My Business account is connected`)),
            withLatestFrom(
              this.store.select(fromApp.getUIReviewTargets),
              (data: ReviewTargetModel, uiReviewTarget: ReviewTargetModel[]) => ({
                ... data,
                priority: uiReviewTarget.find(reviewTarget => reviewTarget.id == data.id).priority,
                isReviewTarget: uiReviewTarget.find(reviewTarget => reviewTarget.id == data.id).isReviewTarget
              })
            ),
            map(data => ReviewTargetsActions.updateGMBReviewTargetByLocationSuccess({ reviewTarget : data })),
            catchError(error => {
              if (error.error.message == GMBFailures.AuthFailure) {
                this.GMBFacadeService.authenticateWithGoogle(null)
                return of(ReviewTargetsActions.reviewTargetError({ error: error.error.message }))
              }
              return of(ReviewTargetsActions.reviewTargetError({ error: error.message }))
            }
            )
          )
      )
    )
  );
  updateGMBReviewTargetByLocationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewTargetsActions.updateGMBReviewTargetByLocationSuccess),
      tap(data=>{
        this.reviewTargetsFacadeService.editReviewTargetByLocation(data, false);
      })
    ),
    { dispatch: false }
  );

  disableOrEnabled$ = createEffect(() =>
  this.actions$.pipe(
    ofType(ReviewTargetsActions.disableOrEnabled),
    map(action => action.request),
    throttleTime(4000),
    switchMap(request =>
      this.httpApiService
        .postRequest(request)
        .pipe(
          map((data) => data as ReviewTargetModel),
          tap(data => this.launchToastMssg('success', 'Update Review Target', `The review target ${data.name} has been updated`)),
          map(reviewTarget => ReviewTargetsActions.disableOrEnabledSuccess({ reviewTarget })),
          tap(() => this.routerFacadeService.navigateTo$('admin/review-target')),
          catchError(error => of(ReviewTargetsActions.reviewTargetError({ error: error.message })))
        )
    )
  )
);

  constructor(
    private actions$: Actions,
    private reviewTargetsService: ReviewTargetsService,
    private reviewTargetsFacadeService: ReviewTargetsFacadeService,
    private routerFacadeService: RouterFacadeService,
    private GMBFacadeService: GMBFacadeService,
    private httpApiService: HttpApiService,
    private messageToast: MessageService,
    private store: Store<any>
  ) { }

  launchToastMssg(severity: string, summary: string, detail: string) {
    this.messageToast.add({ severity: severity, summary: summary, detail: detail, life:5000 });
  }
}